import { useParams, Link } from 'react-router-dom';
import { animeList, animeDetailMap, type AnimeDetail } from '@/mocks/anime';
import { useState } from 'react';

export default function AnimeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const anime = animeList.find(a => a.slug === slug);
  const detail: AnimeDetail | undefined = slug ? animeDetailMap[slug] : undefined;
  const [activeTab, setActiveTab] = useState<'intro' | 'episodes'>('intro');

  if (!anime) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-foreground-300 text-4xl"></i>
          </div>
          <p className="text-foreground-500 mb-4">未找到该动漫</p>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16">
      <article>
        <section className="bg-background-100 py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${anime.category}`} className="hover:text-primary-500 transition-colors">动漫分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">{anime.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={anime.cover}
                    alt={anime.title}
                    className="w-full h-full object-cover"
                    title={`${anime.title} - 樱花动漫网免费在线观看`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">{anime.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-primary-500 text-background-50 text-xs px-2.5 py-1 rounded-full font-medium">{anime.rating}分</span>
                  <span className="text-sm text-foreground-600">{anime.status}</span>
                  <span className="text-sm text-foreground-500">&middot;</span>
                  <span className="text-sm text-foreground-600">{anime.episodes}集</span>
                  <span className="text-sm text-foreground-500">&middot;</span>
                  <span className="text-sm text-foreground-600">{anime.year}年</span>
                  <span className="text-sm text-foreground-500">&middot;</span>
                  <span className="text-sm text-foreground-600">{anime.season}番</span>
                  {anime.isNew && (
                    <span className="bg-accent-100 text-accent-700 text-xs px-2 py-0.5 rounded-full">新番</span>
                  )}
                  {anime.isHot && (
                    <span className="bg-accent-100 text-accent-700 text-xs px-2 py-0.5 rounded-full">热门</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-2">{anime.description}</p>

                <div className="flex items-center gap-5 mt-4 text-xs text-foreground-500 flex-wrap">
                  <span><i className="ri-eye-line mr-1"></i>{(anime.views / 10000).toFixed(1)}万次观看</span>
                  {detail && (
                    <>
                      <span><i className="ri-user-line mr-1"></i>导演：{detail.director}</span>
                      <span><i className="ri-building-line mr-1"></i>制作：{detail.studio}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
              <button
                onClick={() => setActiveTab('intro')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'intro' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                动漫介绍
              </button>
              <button
                onClick={() => setActiveTab('episodes')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'episodes' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                剧集列表
              </button>
            </div>

            {activeTab === 'intro' && detail && (
              <div>
                <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                  <p>{detail.fullDescription}</p>
                </div>
                {detail.voiceActors.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-heading text-sm text-foreground-800 mb-3">主要声优</h3>
                    <div className="flex flex-wrap gap-2">
                      {detail.voiceActors.map((va) => (
                        <span key={va} className="text-xs bg-secondary-50 text-secondary-700 border border-secondary-200/70 px-3 py-1.5 rounded-full">
                          {va}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'episodes' && detail && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {detail.episodeList.map((ep) => (
                  <div
                    key={ep.ep}
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50/50 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      {ep.ep}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-700 truncate">{ep.title}</p>
                      <p className="text-xs text-foreground-500">{ep.duration}</p>
                    </div>
                    <div className="w-5 h-5 flex items-center justify-center ml-auto flex-shrink-0">
                      <i className="ri-play-circle-line text-primary-500 text-lg"></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {detail && detail.relatedAnime.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                <i className="ri-links-line text-accent-500 mr-2"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {detail.relatedAnime.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/anime/${ra.slug}`}
                    className="group flex gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors"
                  >
                    <div className="w-14 h-18 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img src={ra.cover} alt={ra.title} className="w-full h-full object-cover" title={`${ra.title} - 樱花动漫网`} />
                    </div>
                    <div className="min-w-0 flex flex-col justify-between">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">{ra.title}</h3>
                      <p className="text-xs text-foreground-500">{ra.rating}分 &middot; {ra.episodes}集</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </main>
  );
}